import {
  CurrencyCode,
  FreightTerm,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';

import { QuoteDetailRecord } from '../types/quote';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';
import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '~/shared/utils/dateTime';

export const quoteDetailQueryAtom = createQueryAtomFactory(
  'brokerage.quoteDetail',
  (params: { id: string }) => ({
    quote: {
      __args: {
        id: params.id,
      },
      id: true,
      documentNumber: true,
      type: true,
      status: true,
      accountId: true,
      account: {
        id: true,
        name: true,
      },
      facilityId: true,
      facility: {
        id: true,
        facilityName: true,
      },
      subtotal: {
        amount: true,
        currencyCode: true,
      },
      validUntil: true,
      quoteDeadline: true,
      expectedShipDate: true,
      paymentTerm: true,
      paymentCurrency: true,
      freightTerm: true,
      freightCoveredByCustomer: true,
      terms: true,
      notes: true,
      convertedToOrderId: true,
      lineItems: {
        id: true,
        itemId: true,
        item: {
          id: true,
          name: true,
        },
        packageFormat: true,
        quantity: true,
        unit: true,
        unitPrice: {
          amount: true,
          currencyCode: true,
        },
        totalPrice: {
          amount: true,
          currencyCode: true,
        },
        notes: true,
      },
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
  {
    select: (data): QuoteDetailRecord | null => {
      if (!data?.quote) {
        return null;
      }

      const quote = data.quote;
      return {
        id: quote.id,
        quoteNumber: quote.documentNumber,
        type: quote.type,
        status: quote.status,
        accountId: quote.accountId,
        account: quote.account,
        facilityId: quote.facilityId ?? null,
        facility: quote.facility
          ? {
              id: quote.facility.id,
              facilityName: quote.facility.facilityName ?? null,
            }
          : null,
        subtotal: quote.subtotal,
        validUntil: maybeParseCalendarDate(quote.validUntil) ?? null,
        quoteDeadline: maybeParseCalendarDate(quote.quoteDeadline) ?? null,
        expectedShipDate:
          maybeParseCalendarDate(quote.expectedShipDate) ?? null,
        paymentTerm: (quote.paymentTerm as PaymentTerm) ?? null,
        paymentCurrency: (quote.paymentCurrency as CurrencyCode) ?? null,
        freightTerm: (quote.freightTerm as FreightTerm) ?? null,
        freightCoveredByCustomer: quote.freightCoveredByCustomer ?? null,
        terms: quote.terms ?? null,
        notes: quote.notes ?? null,
        convertedToOrderId: quote.convertedToOrderId ?? null,
        lineItems: (quote.lineItems ?? []).map((item) => ({
          id: item.id,
          itemId: item.itemId ?? null,
          item: item.item ?? null,
          packageFormat: item.packageFormat ?? null,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          totalPrice: item.totalPrice,
          notes: item.notes ?? null,
        })),
        timestamps: {
          createdAt: parseZonedDateTime(quote.timestamps.createdAt),
          updatedAt: parseZonedDateTime(quote.timestamps.updatedAt),
        },
      };
    },
  },
);
